import { Link, createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import * as z from "zod/v4";
import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsUpDownIcon,
  FilterIcon,
  SearchIcon,
} from "lucide-react";
import { DateRangeFilter } from "../-components";
import type { ColumnDef } from "@tanstack/react-table";
import type {
  AppSession,
  DetectionFilters,
  SessionPii,
  UsageApp,
} from "@/lib/queries/desktop-statistics";
import { departmentQueryOptions } from "@/lib/queries/departments";
import {
  entitiesFilterQueryOptions,
  sessionPiisQueryOptions,
  usageAppsFilterQueryOptions,
  useActionsFilter,
  useSeveritiesFilter,
  userAppSessionsInfiniteQueryOptions,
  userUsageAppsInfiniteQueryOptions,
} from "@/lib/queries/desktop-statistics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { BreadcrumbNav } from "@/components/ui/breadcrumb";
import { GenericTable } from "@/components/generic-table";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

function formatDate(d: Date) {
  return d.toISOString().slice(0, 10);
}
const DEFAULT_END_DATE = formatDate(new Date());
const DEFAULT_START_DATE = formatDate(
  new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
);
const DEFAULT_ORDER_BY = "-latest_activity";
const PII_PER_PAGE = 10;

function formatDateTime(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const searchSchema = z.object({
  start_date: z.string().default(DEFAULT_START_DATE).catch(DEFAULT_START_DATE),
  end_date: z.string().default(DEFAULT_END_DATE).catch(DEFAULT_END_DATE),
  search: z.string().optional().catch(undefined),
  app_name: z.string().optional().catch(undefined),
  entity_id: z.coerce.number().optional().catch(undefined),
  entity_severity: z.string().optional().catch(undefined),
  entity_action: z.string().optional().catch(undefined),
  order_by: z.string().default(DEFAULT_ORDER_BY).catch(DEFAULT_ORDER_BY),
  user_name: z.string().optional().catch(undefined),
});

export const Route = createFileRoute(
  "/_app/edge-device-statistics/$depId/$userId",
)({
  params: {
    parse: z.object({
      depId: z.coerce.number().int(),
      userId: z.string(),
    }).parse,
  },
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  staticData: {
    permissionGuards: [
      {
        resource: "statistics",
      },
    ],
  },
});

function RouteComponent() {
  const { depId, userId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  function updateSearch(updates: Partial<typeof search>) {
    navigate({ to: ".", search: { ...search, ...updates } });
  }

  const departmentQuery = useQuery(departmentQueryOptions(depId));
  const departmentName = departmentQuery.data?.name ?? "";

  const [rawSearch, setRawSearch] = useState(search.search ?? "");
  const [debouncedSearch] = useDebouncedValue(rawSearch, { wait: 300 });
  useEffect(() => {
    const next = debouncedSearch || undefined;
    if (next !== search.search) updateSearch({ search: next });
  }, [debouncedSearch]);

  const filters = useMemo<DetectionFilters>(
    () => ({
      start_date: search.start_date,
      end_date: search.end_date,
      search: search.search,
      app_name: search.app_name,
      entity_id: search.entity_id,
      entity_severity: search.entity_severity,
      entity_action: search.entity_action,
      order_by: search.order_by,
    }),
    [
      search.start_date,
      search.end_date,
      search.search,
      search.app_name,
      search.entity_id,
      search.entity_severity,
      search.entity_action,
      search.order_by,
    ],
  );

  const activeFilterCount = [
    search.app_name,
    search.entity_id,
    search.entity_severity,
    search.entity_action,
  ].filter((v) => v !== undefined && v !== "").length;

  return (
    <div className="flex w-full flex-col gap-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <BreadcrumbNav
            items={[
              {
                label: "Edge Device Statistics",
                link: (
                  <Link to="/edge-device-statistics">
                    Edge Device Statistics
                  </Link>
                ),
              },
              {
                label: departmentName,
                link: (
                  <Link to="/edge-device-statistics/$depId" params={{ depId }}>
                    {departmentName}
                  </Link>
                ),
              },
              { label: search.user_name ?? "User" },
            ]}
          />
          <h1 className="mt-2 text-3xl font-bold">Detection log</h1>
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
        <div className="relative w-full sm:w-64">
          <SearchIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
            placeholder="Search"
            className="pl-9"
          />
        </div>
        <DateRangeFilter
          startDate={search.start_date}
          endDate={search.end_date}
          onApply={(start_date, end_date) =>
            updateSearch({ start_date, end_date })
          }
        />
        <FilterPopover
          userId={userId}
          search={search}
          activeCount={activeFilterCount}
          onApply={(f) => updateSearch(f)}
        />
      </div>

      <UsageAppsList userId={userId} filters={filters} />
    </div>
  );
}

function UsageAppsList({
  userId,
  filters,
}: {
  userId: string;
  filters: DetectionFilters;
}) {
  const query = useInfiniteQuery(
    userUsageAppsInfiniteQueryOptions(userId, filters),
  );

  const apps = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  if (query.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2 text-muted-foreground">
        <p>Error loading usage apps.</p>
        <pre className="max-w-xl text-xs text-destructive">
          {String(query.error)}
        </pre>
      </div>
    );
  }

  if (!apps.length) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        No apps found for the selected filters.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {apps.map((app) => (
        <AppRow
          key={app.app_name}
          userId={userId}
          app={app}
          filters={filters}
        />
      ))}
      <ShowMore
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        onClick={() => query.fetchNextPage()}
      />
    </div>
  );
}

function AppRow({
  userId,
  app,
  filters,
}: {
  userId: string;
  app: UsageApp;
  filters: DetectionFilters;
}) {
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  return (
    <Collapsible
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setHasOpened(true);
      }}
      className="rounded-lg border border-border bg-card"
    >
      <CollapsibleTrigger className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-3.5 text-left sm:px-4">
        <ChevronRightIcon
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90",
          )}
        />
        <span className="min-w-0 truncate font-medium capitalize">
          {app.app_name}
        </span>
        <Badge variant="secondary" className="shrink-0 rounded-full">
          {app.session_count.toLocaleString()} sessions
        </Badge>
        <span className="ml-auto shrink-0 text-xs whitespace-nowrap text-muted-foreground">
          {formatDateTime(app.last_activity)}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-t border-border px-2 py-3 sm:px-4">
          {hasOpened && (
            <AppSessionsList
              userId={userId}
              appName={app.app_name}
              filters={filters}
            />
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function AppSessionsList({
  userId,
  appName,
  filters,
}: {
  userId: string;
  appName: string;
  filters: DetectionFilters;
}) {
  const query = useInfiniteQuery(
    userAppSessionsInfiniteQueryOptions(userId, appName, filters),
  );

  const sessions = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  if (query.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <p className="py-2 text-sm text-destructive">Error loading sessions.</p>
    );
  }

  if (!sessions.length) {
    return (
      <p className="py-2 text-sm text-muted-foreground">No sessions found.</p>
    );
  }

  return (
    <div className="space-y-2">
      {sessions.map((session) => (
        <SessionRow
          key={session.session_id}
          userId={userId}
          session={session}
          filters={filters}
        />
      ))}
      <ShowMore
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        onClick={() => query.fetchNextPage()}
      />
    </div>
  );
}

function SessionRow({
  userId,
  session,
  filters,
}: {
  userId: string;
  session: AppSession;
  filters: DetectionFilters;
}) {
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  return (
    <Collapsible
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setHasOpened(true);
      }}
      className="rounded-lg border border-border bg-background"
    >
      <CollapsibleTrigger className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-3 text-left sm:px-4">
        <ChevronRightIcon
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90",
          )}
        />
        <span className="min-w-0 text-sm font-medium">
          Session
          <span className="ml-2 font-normal text-muted-foreground">
            {formatDateTime(session.origin_timestamp)}
          </span>
        </span>
        <StatusBadge variant="warning-error" className="ml-auto shrink-0">
          {session.pii_count} PII
        </StatusBadge>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-t border-border p-2 sm:p-3">
          {hasOpened && (
            <SessionPiisTable
              userId={userId}
              sessionId={session.session_id}
              filters={filters}
            />
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function severityVariant(severity: string) {
  switch (severity.toUpperCase()) {
    case "CRITICAL":
      return "error" as const;
    case "WATCH":
      return "warning-error" as const;
    default:
      return "default" as const;
  }
}

function actionVariant(action: string) {
  return action.toLowerCase() === "prevented"
    ? ("success" as const)
    : ("info" as const);
}

type PiiTableMeta = {
  severityLabel: (value: string) => string;
  actionLabel: (value: string) => string;
};

const piiColumns: Array<ColumnDef<SessionPii>> = [
  {
    id: "timestamp",
    header: "Timestamp",
    accessorKey: "origin_timestamp",
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground tabular-nums">
        {formatDateTime(row.original.origin_timestamp)}
      </span>
    ),
  },
  {
    id: "data_type",
    header: "Data type detected",
    cell: ({ row }) => (
      <span className="font-medium">{row.original.entity_type.name}</span>
    ),
  },
  {
    id: "severity",
    header: "Severity",
    cell: ({ row, table }) => {
      const meta = table.options.meta as PiiTableMeta | undefined;
      const { severity } = row.original;
      return (
        <StatusBadge variant={severityVariant(severity)}>
          {meta?.severityLabel(severity) ?? severity}
        </StatusBadge>
      );
    },
  },
  {
    id: "action",
    header: "Action taken",
    cell: ({ row, table }) => {
      const meta = table.options.meta as PiiTableMeta | undefined;
      const { action_type } = row.original;
      return (
        <StatusBadge variant={actionVariant(action_type)}>
          {meta?.actionLabel(action_type) ?? action_type}
        </StatusBadge>
      );
    },
  },
];

function SessionPiisTable({
  userId,
  sessionId,
  filters,
}: {
  userId: string;
  sessionId: string;
  filters: DetectionFilters;
}) {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const query = useQuery(
    sessionPiisQueryOptions(userId, sessionId, filters, page, PII_PER_PAGE),
  );

  const severitiesQuery = useSeveritiesFilter();
  const actionsQuery = useActionsFilter();

  const severityLabels = useMemo(
    () => new Map((severitiesQuery.data ?? []).map((s) => [s.value, s.label])),
    [severitiesQuery.data],
  );
  const actionLabels = useMemo(
    () => new Map((actionsQuery.data ?? []).map((a) => [a.value, a.label])),
    [actionsQuery.data],
  );

  const items = query.data?.items ?? [];
  const count = query.data?.count ?? 0;
  const pageCount = Math.max(Math.ceil(count / PII_PER_PAGE), 1);

  const table = useReactTable({
    data: items,
    columns: piiColumns,
    filterFns: { fuzzy: fuzzyFilter },
    initialState: { pagination: { pageIndex: 0, pageSize: 5 } },
    meta: {
      severityLabel: (value: string) => severityLabels.get(value) ?? value,
      actionLabel: (value: string) => actionLabels.get(value) ?? value,
    } satisfies PiiTableMeta,
    getCoreRowModel: getCoreRowModel(),
  });

  const start = count === 0 ? 0 : (page - 1) * PII_PER_PAGE + 1;
  const end = Math.min(page * PII_PER_PAGE, count);

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-border">
        <GenericTable table={table} isLoading={query.isLoading} />
      </div>
      {count > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground tabular-nums">
            {start}-{end} of {count.toLocaleString()}
          </span>
          <NumberPagination
            page={page}
            pageCount={pageCount}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}

type PageItem = number | "ellipsis-left" | "ellipsis-right";

function pageRange(
  current: number,
  total: number,
  sibling = 1,
): Array<PageItem> {
  const totalVisible = sibling * 2 + 5;
  const range = (start: number, end: number) =>
    end < start
      ? []
      : Array.from({ length: end - start + 1 }, (_, i) => start + i);

  if (total <= totalVisible) return range(1, total);

  const left = Math.max(current - sibling, 1);
  const right = Math.min(current + sibling, total);
  const showLeft = left > 2;
  const showRight = right < total - 1;

  if (!showLeft && showRight) {
    return [...range(1, 3 + sibling * 2), "ellipsis-right", total];
  }
  if (showLeft && !showRight) {
    return [1, "ellipsis-left", ...range(total - (3 + sibling * 2) + 1, total)];
  }
  return [1, "ellipsis-left", ...range(left, right), "ellipsis-right", total];
}

function NumberPagination({
  page,
  pageCount,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}) {
  if (pageCount <= 1) return null;
  const items = pageRange(page, pageCount);

  return (
    <Pagination className="w-auto justify-end">
      <PaginationContent className="gap-1.5">
        <PaginationItem>
          <Button
            variant="outline"
            size="icon"
            className="size-9 rounded-lg"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label="Go to previous page"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
        </PaginationItem>

        {items.map((item, i) =>
          typeof item === "number" ? (
            <PaginationItem key={`page-${item}`}>
              <Button
                variant="outline"
                size="icon"
                onClick={() => onPageChange(item)}
                aria-current={item === page ? "page" : undefined}
                className={cn(
                  "size-9 rounded-lg font-medium tabular-nums",
                  item === page
                    ? "border-primary bg-[#FCE7F6] hover:bg-[#FCE7F6]"
                    : "",
                )}
              >
                {item}
              </Button>
            </PaginationItem>
          ) : (
            <PaginationItem key={`${item}-${i}`}>
              <PaginationEllipsis className="size-9" />
            </PaginationItem>
          ),
        )}

        <PaginationItem>
          <Button
            variant="outline"
            size="icon"
            className="size-9 rounded-lg"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
            aria-label="Go to next page"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

function ShowMore({
  hasNextPage,
  isFetchingNextPage,
  onClick,
}: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onClick: () => void;
}) {
  if (!hasNextPage) return null;
  return (
    <div className="flex justify-center pt-1">
      <Button
        variant="outline"
        size="sm"
        onClick={onClick}
        disabled={isFetchingNextPage}
      >
        {isFetchingNextPage ? "Loading..." : "Show more"}
      </Button>
    </div>
  );
}

type FilterValues = {
  app_name?: string;
  entity_id?: number;
  entity_severity?: string;
  entity_action?: string;
  order_by: string;
};

const ORDER_BY_OPTIONS = [
  { value: "-latest_activity", label: "Latest activity (newest)" },
  { value: "latest_activity", label: "Latest activity (oldest)" },
  { value: "-session_count", label: "Sessions (high to low)" },
  { value: "session_count", label: "Sessions (low to high)" },
];

function FilterPopover({
  userId,
  search,
  activeCount,
  onApply,
}: {
  userId: string;
  search: z.infer<typeof searchSchema>;
  activeCount: number;
  onApply: (values: FilterValues) => void;
}) {
  const [open, setOpen] = useState(false);
  const [appName, setAppName] = useState(search.app_name ?? "");
  const [entityId, setEntityId] = useState(search.entity_id?.toString() ?? "");
  const [entityLabel, setEntityLabel] = useState("");
  const [severity, setSeverity] = useState(search.entity_severity ?? "");
  const [action, setAction] = useState(search.entity_action ?? "");
  const [orderBy, setOrderBy] = useState(search.order_by);

  const severitiesQuery = useSeveritiesFilter();
  const actionsQuery = useActionsFilter();

  function reset() {
    setAppName("");
    setEntityId("");
    setEntityLabel("");
    setSeverity("");
    setAction("");
    setOrderBy(DEFAULT_ORDER_BY);
  }

  function handleOpenChange(next: boolean) {
    if (next) {
      setAppName(search.app_name ?? "");
      setEntityId(search.entity_id?.toString() ?? "");
      setSeverity(search.entity_severity ?? "");
      setAction(search.entity_action ?? "");
      setOrderBy(search.order_by);
    }
    setOpen(next);
  }

  function handleApply() {
    onApply({
      app_name: appName || undefined,
      entity_id: entityId ? Number(entityId) : undefined,
      entity_severity: severity || undefined,
      entity_action: action || undefined,
      order_by: orderBy || DEFAULT_ORDER_BY,
    });
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="relative gap-2">
          <FilterIcon className="h-4 w-4" />
          Filters
          {activeCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">Filters</p>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              onClick={reset}
            >
              Reset all
            </Button>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              App
            </label>
            <AppNameCombobox
              userId={userId}
              value={appName}
              onValueChange={setAppName}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Entity
            </label>
            <EntityCombobox
              value={entityId}
              label={entityLabel}
              onValueChange={(value, label) => {
                setEntityId(value);
                setEntityLabel(label);
              }}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Severity
            </label>
            <Select
              value={severity || "_all"}
              onValueChange={(v) => setSeverity(v === "_all" ? "" : v)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="All severities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All severities</SelectItem>
                {severitiesQuery.data?.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Action
            </label>
            <Select
              value={action || "_all"}
              onValueChange={(v) => setAction(v === "_all" ? "" : v)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All actions</SelectItem>
                {actionsQuery.data?.map((a) => (
                  <SelectItem key={a.value} value={a.value}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Order by
            </label>
            <Select value={orderBy} onValueChange={setOrderBy}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORDER_BY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button className="flex-1" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AppNameCombobox({
  userId,
  value,
  onValueChange,
}: {
  userId: string;
  value: string;
  onValueChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [rawSearch, setRawSearch] = useState("");
  const [searchTerm] = useDebouncedValue(rawSearch, { wait: 250 });

  const appsQuery = useQuery(
    usageAppsFilterQueryOptions(userId, { search: searchTerm }),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">{value || "All apps"}</span>
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search apps..."
            value={rawSearch}
            onValueChange={setRawSearch}
          />
          <CommandList>
            {appsQuery.isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            ) : (
              <>
                <CommandEmpty>No apps found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="__all__"
                    onSelect={() => {
                      onValueChange("");
                      setOpen(false);
                    }}
                  >
                    <CheckIcon
                      className={cn(
                        "mr-2 h-4 w-4",
                        !value ? "opacity-100" : "opacity-0",
                      )}
                    />
                    All apps
                  </CommandItem>
                  {appsQuery.data?.map((app) => (
                    <CommandItem
                      key={app}
                      value={app}
                      onSelect={() => {
                        onValueChange(app === value ? "" : app);
                        setOpen(false);
                      }}
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === app ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {app}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function EntityCombobox({
  value,
  label,
  onValueChange,
}: {
  value: string;
  label: string;
  onValueChange: (value: string, label: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [rawSearch, setRawSearch] = useState("");
  const [searchTerm] = useDebouncedValue(rawSearch, { wait: 250 });

  const entitiesQuery = useQuery(
    entitiesFilterQueryOptions({ search: searchTerm }),
  );

  const displayLabel =
    label || (value ? `Entity #${value}` : "") || "All entities";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {value ? displayLabel : "All entities"}
          </span>
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search entities..."
            value={rawSearch}
            onValueChange={setRawSearch}
          />
          <CommandList>
            {entitiesQuery.isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            ) : (
              <>
                <CommandEmpty>No entities found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="__all__"
                    onSelect={() => {
                      onValueChange("", "");
                      setOpen(false);
                    }}
                  >
                    <CheckIcon
                      className={cn(
                        "mr-2 h-4 w-4",
                        !value ? "opacity-100" : "opacity-0",
                      )}
                    />
                    All entities
                  </CommandItem>
                  {entitiesQuery.data?.map((entity) => {
                    const idStr = entity.id.toString();
                    return (
                      <CommandItem
                        key={entity.id}
                        value={idStr}
                        keywords={[entity.name]}
                        onSelect={() => {
                          const isSame = idStr === value;
                          onValueChange(
                            isSame ? "" : idStr,
                            isSame ? "" : entity.name,
                          );
                          setOpen(false);
                        }}
                      >
                        <CheckIcon
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === idStr ? "opacity-100" : "opacity-0",
                          )}
                        />
                        {entity.name}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
